import { format, isValid, parseISO } from 'date-fns'

import {
  DistributionChannel,
  IDate,
  Speaker,
  TimelineChannel,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import {
  distributionChannelLabel,
  timeAgoFromToday,
} from '@avantstay/backoffice-core'

import { getChannelTone } from './helpers/channelTone'
import { OTA_REPLY_CHANNELS } from './StayTimeline.consts'
import {
  ChannelTone,
  GuestInboxStayDetail,
  GuestInboxStayFacts,
  GuestInboxTimelineConversation,
  GuestInboxTimelineEvent,
  TimelineFilterValues,
} from './StayTimeline.types'

const MS_PER_DAY = 86_400_000

const MACHINE_SPEAKERS = [Speaker.automation, Speaker.bot, Speaker.system]
const OUTBOUND_SPEAKERS = [Speaker.teammate, Speaker.vendor, Speaker.both]

const MACHINE_AUTHOR = /duckie|\bai\b|automat|^bot\b|no.?reply|system/i
const MACHINE_TEXT =
  /^(the following message was automatically sent to the guest:|reservation confirmed|the guest listed pets)/i

export const getOtaReplyLabel = (channel?: DistributionChannel) => {
  return channel && OTA_REPLY_CHANNELS.includes(channel)
    ? distributionChannelLabel[channel]
    : undefined
}

interface ToStayDetailParams {
  facts: GuestInboxStayFacts
  thread: GuestInboxTimelineConversation
}

export const toStayDetail = ({ facts, thread }: ToStayDetailParams): GuestInboxStayDetail => ({
  ...facts,
  ...thread,
  booking: { ...facts.booking, ...thread.booking },
})

export const toDate = (value?: IDate | null) => {
  if (!value) return undefined

  const parsed = typeof value === 'string' ? parseISO(value) : value
  return isValid(parsed) ? parsed : undefined
}

export const formatHours = (hours?: number | null) => {
  if (hours == null) return '—'
  if (hours < 1) return 'under an hour'
  if (hours < 24) return `${Math.round(hours)}h`
  return `${Math.round(hours / 24)}d`
}

export const waitLabel = (hours?: number | null) => {
  if (hours == null) return '—'
  if (hours < 1) return 'just now'
  if (hours < 24) return `${Math.round(hours)}h`
  return `${Math.round(hours / 24)}d`
}

export const getEventWhen = (
  at?: IDate | null,
  checkIn?: IDate | null,
  checkout?: IDate | null,
) => {
  const date = toDate(at)
  if (!date) return ''

  const relative = timeAgoFromToday(date)
  const checkInDate = toDate(checkIn)
  const checkoutDate = toDate(checkout)

  if (checkInDate) {
    const daysBefore = Math.round((checkInDate.getTime() - date.getTime()) / MS_PER_DAY)
    if (daysBefore >= 1) return `${relative} · ${daysBefore}d before check-in`
  }

  if (checkoutDate && date > checkoutDate) return `${relative} · after checkout`

  return `${relative} · ${format(date, 'h:mm a')}`
}

export const isAutomationEvent = (event: GuestInboxTimelineEvent) =>
  event.speaker === Speaker.automation

export interface TimelineSplit {
  conversation: GuestInboxTimelineEvent[]
  notes: GuestInboxTimelineEvent[]
}

/** An internal note a teammate wrote; machine-generated internal events are not notes. */
export const isTeamNote = (event: GuestInboxTimelineEvent) => {
  if (!event.internal) return false
  if (event.noteId) return true
  if (MACHINE_SPEAKERS.includes(event.speaker)) return false
  if (MACHINE_AUTHOR.test(event.who ?? '')) return false
  if (MACHINE_TEXT.test((event.body ?? '').trim())) return false

  return true
}

export const getEditedLabel = ({ editedAt }: Pick<GuestInboxTimelineEvent, 'editedAt'>) => {
  return editedAt ? `Edited ${timeAgoFromToday(editedAt)}` : undefined
}

export const splitTimeline = (events: GuestInboxTimelineEvent[]): TimelineSplit => ({
  conversation: events.filter(event => !event.internal),
  notes: events.filter(isTeamNote),
})

const isShownChannel = (event: GuestInboxTimelineEvent, channels: ChannelTone[]) => {
  if (!channels.length || event.channel === TimelineChannel.case) return true

  return channels.includes(getChannelTone(event.channel, event.medium))
}

interface GetTimelineEventsProps extends Pick<TimelineFilterValues, 'notes' | 'channels'> {
  events: GuestInboxTimelineEvent[]
}

export const getTimelineEvents = ({ events, notes, channels }: GetTimelineEventsProps) => {
  return events.filter(event => {
    if (event.internal) return notes && isTeamNote(event)
    return isShownChannel(event, channels)
  })
}

export const getLastGuestEvent = (conversation: GuestInboxTimelineEvent[]) =>
  [...conversation].reverse().find(event => event.speaker === Speaker.guest)

export const getLastOutboundEvent = (conversation: GuestInboxTimelineEvent[]) =>
  [...conversation].reverse().find(event => OUTBOUND_SPEAKERS.includes(event.speaker))

interface SourceGapArgs {
  conversation: GuestInboxTimelineEvent[]
  callsCount: number
  spokeOutbound: number
  casesTotal: number
  sendsTotal: number
}

export const hasSourceGap = ({
  conversation,
  callsCount,
  spokeOutbound,
  casesTotal,
  sendsTotal,
}: SourceGapArgs): boolean => {
  const heardFromGuest = callsCount > 0 || conversation.some(item => item.speaker === Speaker.guest)
  const weActed = spokeOutbound > 0 || casesTotal > 0 || sendsTotal > 0

  return !heardFromGuest && weActed
}

interface ClippedSpokeArgs {
  spokeTotal?: number | null
  conversation: GuestInboxTimelineEvent[]
}

export interface ClippedSpoke {
  shown: number
  total: number
}

export const getClippedSpoke = ({
  spokeTotal,
  conversation,
}: ClippedSpokeArgs): ClippedSpoke | undefined => {
  if (spokeTotal == null) return undefined

  const shown = conversation.filter(item => item.channel === TimelineChannel.spoke).length
  if (spokeTotal <= shown) return undefined

  return { shown, total: spokeTotal }
}

export interface TimelineDayGroup<T = GuestInboxTimelineEvent> {
  key: string
  label: string
  events: T[]
}

const UNDATED_KEY = 'undated'

export const groupEventsByDay = <T extends { at?: IDate | null }>(
  events: T[],
): TimelineDayGroup<T>[] => {
  const groups: TimelineDayGroup<T>[] = []
  const undated: T[] = []

  events.forEach(item => {
    const day = toDate(item.at)

    if (!day) {
      undated.push(item)
      return
    }

    const key = format(day, 'yyyy-MM-dd')
    const current = groups[groups.length - 1]

    if (current?.key === key) {
      current.events.push(item)
      return
    }

    groups.push({ key, label: format(day, 'EEEE d MMMM').toUpperCase(), events: [item] })
  })

  if (undated.length) groups.push({ key: UNDATED_KEY, label: 'Undated', events: undated })

  return groups
}
